import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useFormik } from 'formik';

import links from 'appConfig/links';

import { PUBLIC, PRIVATE } from 'router/constants/routes';

import { createErrorSelector } from 'store/errors/selectors';
import { isAuthenticatedSelector } from 'store/auth/selectors';
import { SIGN_IN } from 'store/auth/actionTypes';
import { signInRequest } from 'store/auth/actions';

import {
  sendAnalyticLoginButtonClick,
  sendAnalyticLoginError,
  sendAnalyticLoginSuccess,
  sendAnalyticResetPasswordClick,
} from 'services/analytics';

import { useDispatch, useSelector } from 'react-redux';

import { isEmpty } from 'helpers/dataStructuresUtils';

import { Text, Button, Box } from 'components/library';
import FormikInput from 'components/FormikInput';

import { ForgotPasswordLink } from './styled';

import * as S from 'assets/styles/authStyled';
import loginImage from 'assets/img/loginImage.webp';

import { CONTENT } from './config';

import { LoginSchema } from './validationSchema';
import { RootReducer } from 'store/rootReducer';

type FormValue = { email: string; password: string };

const Login = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isFetching, setFetchingStatus] = useState(false);

  const isAuthenticated = useSelector(isAuthenticatedSelector);
  const error = useSelector(createErrorSelector(SIGN_IN));
  const { redirectUrl } = useSelector((state: RootReducer) => state.auth);

  const successRedirectPath = redirectUrl || PRIVATE.MAIN.path;

  useEffect(() => {
    if (isAuthenticated) {
      navigate(successRedirectPath);
    }
  }, []);

  useEffect(() => {
    if (error?.message) {
      CONTENT.forEach((field) => setFieldError(field.name, error?.message));
    }
  }, [error]);

  const handleSuccessLogin = () => {
    sendAnalyticLoginSuccess();

    navigate(successRedirectPath);
  };

  const handleFailLogin = () => {
    sendAnalyticLoginError();

    CONTENT.forEach((field) => setFieldError(field.name, t('login.error.forbidden')));

    setFetchingStatus(false);
  };

  const { errors, dirty, values, initialValues, setFieldTouched, setFieldValue, touched, handleSubmit, setFieldError } =
    useFormik({
      initialValues: { email: '', password: '' },
      validationSchema: LoginSchema,
      validateOnBlur: true,
      onSubmit: (userData: FormValue) => {
        setFetchingStatus(true);

        sendAnalyticLoginButtonClick();

        dispatch(signInRequest({ userData, onSuccess: handleSuccessLogin, onError: handleFailLogin }));
      },
    });

  return (
    <S.AuthContainer>
      <S.AuthImageBox>
        <S.AuthImage src={loginImage} alt="reset-password" />
      </S.AuthImageBox>
      <S.AuthForm>
        <form>
          <Box $paddingBottom={32} $paddingX={16}>
            <Text type="h5" bold center mb={12}>
              {t('login.title')}
            </Text>
            <Text center mb={4}>
              {t('login.subTitle')}
            </Text>

            <Text type="overline" bold center mb={20}>
              <a href={links.onboarding} data-locator="surveyButton">
                {t('login.survey')}
              </a>
            </Text>
            <Box $mb={44}>
              {CONTENT.map(({ name, label, type, icon, dataLocator, placeholder, analyticHandler }) => (
                <FormikInput
                  key={name}
                  mb={32}
                  type={type}
                  field={name}
                  label={label}
                  setFieldTouched={setFieldTouched}
                  initialValues={initialValues}
                  setFieldValue={setFieldValue}
                  onInputFocus={analyticHandler}
                  touched={touched}
                  values={values}
                  errors={errors}
                  leftIcon={icon}
                  dataLocator={dataLocator}
                  placeholder={placeholder}
                />
              ))}
            </Box>
            <Button
              onClick={handleSubmit}
              disabled={!dirty || !isEmpty(errors) || isFetching}
              textColor="text-light"
              mb={24}
              type="submit"
              isLoading={isFetching}
              dataLocator="loginButton"
            >
              {t('basics.login')}
            </Button>

            <ForgotPasswordLink
              onClick={sendAnalyticResetPasswordClick}
              to={PUBLIC.FORGOT_PASSWORD.path}
              data-locator="ForgotPasswordButton"
            >
              <Text type="overline" center bold mb={20}>
                {t('basics.forgotPassword')}
              </Text>
            </ForgotPasswordLink>
          </Box>
        </form>
      </S.AuthForm>
    </S.AuthContainer>
  );
};

export default Login;
